import type { TimezoneEntry, CityEntry, LocationSlot } from './types.js';

// === Local time representation ===
//
// All local-time math runs in whole minutes, not hours. Half-hour zones
// (India UTC+5:30) and quarter-hour zones (Nepal UTC+5:45) are common enough
// in real teams that fractional-hour arithmetic is not safe here.

export interface LocalTime {
  /** Minutes since local midnight, 0–1439 */
  minutes: number;
  /** Calendar day relative to the UTC day: -1, 0, or +1 */
  dayOffset: number;
}

/** Convert a UTC hour (0-23) into local time for a given UTC offset in hours */
export function toLocal(utcHour: number, offset: number): LocalTime {
  const total = Math.round(utcHour * 60 + offset * 60);
  const dayOffset = Math.floor(total / 1440);
  let minutes = total % 1440;
  if (minutes < 0) minutes += 1440;
  return { minutes, dayOffset };
}

/** Format local minutes as "6:30 PM" */
export function formatLocalMinutes(minutes: number): string {
  const h24 = Math.floor(minutes / 60) % 24;
  const min = minutes % 60;
  const displayHour = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  const ampm = h24 < 12 ? 'AM' : 'PM';
  return `${displayHour}:${String(min).padStart(2, '0')} ${ampm}`;
}

/** Format a local time with a day marker when it lands on a different calendar day */
export function formatLocalTime(lt: LocalTime, withDayMarker = true): string {
  const base = formatLocalMinutes(lt.minutes);
  if (!withDayMarker || lt.dayOffset === 0) return base;
  return `${base} ${lt.dayOffset > 0 ? '(+1d)' : '(−1d)'}`;
}

/** Convert a UTC hour (0-23) to a local time string like "6:30 PM" */
export function formatTime(utcHour: number, offset: number): string {
  return formatLocalMinutes(toLocal(utcHour, offset).minutes);
}

/** Convert UTC hour to local hour as a fractional number (0-23.99) */
export function getLocalHour(utcHour: number, offset: number): number {
  return toLocal(utcHour, offset).minutes / 60;
}

// === DST rules ===

export interface DSTRule {
  month: number; // 1-12
  weekday: number; // 0=Sun
  /** 1-4 for "nth weekday", or 'last' */
  nth: number | 'last';
  hour: number;
  minute: number;
}

const ruleCache = new Map<string, DSTRule | null>();

/**
 * Infer a recurring "nth weekday of month" rule from a single reference date.
 *
 * The dataset stores one concrete transition date per zone (for the data year).
 * Nearly every DST-observing zone transitions on an nth-or-last weekday, so the
 * rule can be recovered from that one date and replayed for any other year.
 * Zones with rules tied to other calendars (Iran's Nowruz, Morocco's Ramadan
 * window) do not follow this pattern — projections for those beyond the data
 * year are approximate, which the recurring audit surfaces to the user.
 */
function inferDSTRule(dateStr: string): DSTRule | null {
  const cached = ruleCache.get(dateStr);
  if (cached !== undefined) return cached;

  let rule: DSTRule | null = null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(dateStr);
  if (m) {
    const year = parseInt(m[1]!, 10);
    const month = parseInt(m[2]!, 10);
    const day = parseInt(m[3]!, 10);
    const hour = parseInt(m[4] ?? '0', 10);
    const minute = parseInt(m[5] ?? '0', 10);

    const d = new Date(Date.UTC(year, month - 1, day));
    const weekday = d.getUTCDay();

    // Is this the last such weekday in the month?
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const isLast = day + 7 > daysInMonth;
    const nth: number | 'last' = isLast ? 'last' : Math.floor((day - 1) / 7) + 1;

    rule = { month, weekday, nth, hour, minute };
  }

  ruleCache.set(dateStr, rule);
  return rule;
}

/** Resolve a DST rule to a concrete UTC-based Date in the given year */
function resolveRule(rule: DSTRule, year: number): Date {
  const daysInMonth = new Date(Date.UTC(year, rule.month, 0)).getUTCDate();

  if (rule.nth === 'last') {
    const lastDay = new Date(Date.UTC(year, rule.month - 1, daysInMonth));
    const back = (lastDay.getUTCDay() - rule.weekday + 7) % 7;
    const day = daysInMonth - back;
    return new Date(Date.UTC(year, rule.month - 1, day, rule.hour, rule.minute));
  }

  const first = new Date(Date.UTC(year, rule.month - 1, 1));
  const forward = (rule.weekday - first.getUTCDay() + 7) % 7;
  const day = 1 + forward + (rule.nth - 1) * 7;
  return new Date(Date.UTC(year, rule.month - 1, day, rule.hour, rule.minute));
}

/**
 * Resolve a stored transition date for a given year.
 *
 * Note: the stored clock time is the local wall-clock time of the transition;
 * treating it as UTC shifts the boundary by a few hours. That is immaterial at
 * the day granularity this tool works in.
 */
function parseDSTDate(dateStr: string, year: number): Date | null {
  const rule = inferDSTRule(dateStr);
  if (!rule) return null;
  return resolveRule(rule, year);
}

/** The inferred spring-forward / fall-back rules for a zone, if it observes DST */
export function getDSTRules(tz: TimezoneEntry): { start: DSTRule; end: DSTRule } | null {
  if (!tz.dst || !tz.dst_start || !tz.dst_end) return null;
  const start = inferDSTRule(tz.dst_start);
  const end = inferDSTRule(tz.dst_end);
  if (!start || !end) return null;
  return { start, end };
}

/** True when the zone's DST rule cannot be replayed reliably across years */
export function hasIrregularDSTRule(tz: TimezoneEntry): boolean {
  // Rules anchored to non-Gregorian calendars move by more than a week per year.
  return /^(Asia\/Tehran|Africa\/Casablanca|Africa\/El_Aaiun)$/.test(tz.id);
}

/** Check if DST is active for a timezone at a given instant */
export function isDSTActive(tz: TimezoneEntry, at: Date = new Date()): boolean {
  if (!tz.dst || !tz.dst_start || !tz.dst_end) return false;

  const year = at.getUTCFullYear();
  const dstStart = parseDSTDate(tz.dst_start, year);
  const dstEnd = parseDSTDate(tz.dst_end, year);
  if (!dstStart || !dstEnd) return false;

  // Southern hemisphere: DST spans the year boundary (starts Oct, ends Apr)
  if (dstStart > dstEnd) {
    return at >= dstStart || at < dstEnd;
  }
  return at >= dstStart && at < dstEnd;
}

/** Get the UTC offset for a timezone at a given instant, accounting for DST */
export function getCurrentOffset(tz: TimezoneEntry, at: Date = new Date()): number {
  if (!tz.dst) return tz.utc_offset;
  if (isDSTActive(tz, at)) {
    return tz.utc_offset + (tz.dst_offset ?? 1);
  }
  return tz.utc_offset;
}

/** The timezone abbreviation in effect at a given instant */
export function getAbbr(tz: TimezoneEntry, at: Date = new Date()): string {
  return isDSTActive(tz, at) ? (tz.abbr_dst ?? tz.abbr) : tz.abbr;
}

// === Working hours ===

/** Check if a local time (in minutes) falls within working hours */
export function isWorkingMinutes(minutes: number, workStart: number, workEnd: number): boolean {
  const start = workStart * 60;
  const end = workEnd * 60;
  if (start <= end) return minutes >= start && minutes < end;
  // Overnight shift (e.g. 22:00–06:00)
  return minutes >= start || minutes < end;
}

/** Check if a local hour falls within working hours */
export function isWorkingHour(localHour: number, workStart: number, workEnd: number): boolean {
  return isWorkingMinutes(Math.round(localHour * 60), workStart, workEnd);
}

/** Hours between a local time and the nearest edge of the working window (0 if inside) */
function hoursOutsideWindow(minutes: number, workStart: number, workEnd: number): number {
  if (isWorkingMinutes(minutes, workStart, workEnd)) return 0;
  const start = workStart * 60;
  const end = workEnd * 60;
  const circular = (a: number, b: number) => {
    const d = Math.abs(a - b);
    return Math.min(d, 1440 - d);
  };
  return Math.min(circular(minutes, start), circular(minutes, end)) / 60;
}

/** Get the 24-hour timeline of working status for a slot */
export function getTimelineBlocks(slot: LocationSlot, at: Date = new Date()): boolean[] {
  const offset = getCurrentOffset(slot.timezone, at);
  const blocks: boolean[] = [];
  for (let utcHour = 0; utcHour < 24; utcHour++) {
    const local = toLocal(utcHour, offset);
    blocks.push(isWorkingMinutes(local.minutes, slot.workingStart, slot.workingEnd));
  }
  return blocks;
}

/** Get overlap blocks: UTC hours where ALL slots have working time */
export function getOverlapBlocks(slots: LocationSlot[], at: Date = new Date()): boolean[] {
  if (slots.length === 0) return new Array(24).fill(false);

  const overlap: boolean[] = new Array(24).fill(true);
  for (const slot of slots) {
    const blocks = getTimelineBlocks(slot, at);
    for (let i = 0; i < 24; i++) {
      if (!blocks[i]) overlap[i] = false;
    }
  }
  return overlap;
}

// === Fairness scoring ===
//
// A meeting hour is not simply "works" or "doesn't". An hour that is 30 minutes
// past someone's workday is a different proposition from one that wakes them at
// 3 AM, and ranking has to reflect that — otherwise the tool quietly recommends
// times that cost one person their sleep. Unsociable hours (22:00–07:00 local)
// carry a penalty an order of magnitude above fringe hours, so no amount of
// mild convenience for the rest of the team outweighs one person's night.

/** Local hours considered unsociable regardless of stated working hours */
const NIGHT_START_MIN = 22 * 60;
const NIGHT_END_MIN = 7 * 60;

const FRINGE_PENALTY_PER_HOUR = 3;
const NIGHT_BASE_PENALTY = 40;
const NIGHT_PENALTY_PER_HOUR = 6;

export type HourStatus = 'working' | 'fringe' | 'unsociable';

export interface SlotHourEval {
  status: HourStatus;
  /** Higher is worse */
  penalty: number;
  /** Hours between this time and the edge of the person's working window */
  hoursOutside: number;
  local: LocalTime;
}

export interface RankedHour {
  utcHour: number;
  perSlot: SlotHourEval[];
  /** Total burden across all participants; lower is better */
  score: number;
  /** The single worst individual penalty at this hour */
  worstPenalty: number;
  /** True when every participant is inside their working hours */
  fullOverlap: boolean;
  /** Participants outside their working hours, by slot index */
  strainedIndices: number[];
}

function evaluateSlotHour(slot: LocationSlot, utcHour: number, at: Date): SlotHourEval {
  const offset = getCurrentOffset(slot.timezone, at);
  const local = toLocal(utcHour, offset);
  const hoursOutside = hoursOutsideWindow(local.minutes, slot.workingStart, slot.workingEnd);

  if (hoursOutside === 0) {
    return { status: 'working', penalty: 0, hoursOutside: 0, local };
  }

  const isNight = local.minutes >= NIGHT_START_MIN || local.minutes < NIGHT_END_MIN;
  if (isNight) {
    return {
      status: 'unsociable',
      penalty: NIGHT_BASE_PENALTY + NIGHT_PENALTY_PER_HOUR * hoursOutside,
      hoursOutside,
      local,
    };
  }

  return {
    status: 'fringe',
    penalty: FRINGE_PENALTY_PER_HOUR * hoursOutside,
    hoursOutside,
    local,
  };
}

/**
 * Evaluate all 24 UTC hours, best-first.
 *
 * Every hour is returned, including ones nobody can make — when a team has no
 * true overlap (San Francisco / Singapore has none), the least-bad options are
 * the answer, and refusing to rank them just moves the guesswork back onto the
 * user.
 */
export function rankMeetingHours(slots: LocationSlot[], at: Date = new Date()): RankedHour[] {
  const ranked: RankedHour[] = [];

  for (let utcHour = 0; utcHour < 24; utcHour++) {
    const perSlot = slots.map(slot => evaluateSlotHour(slot, utcHour, at));
    const score = perSlot.reduce((sum, e) => sum + e.penalty, 0);
    const worstPenalty = perSlot.reduce((max, e) => Math.max(max, e.penalty), 0);
    const strainedIndices = perSlot
      .map((e, i) => (e.status === 'working' ? -1 : i))
      .filter(i => i >= 0);

    ranked.push({
      utcHour,
      perSlot,
      score,
      worstPenalty,
      fullOverlap: strainedIndices.length === 0,
      strainedIndices,
    });
  }

  // Lowest total burden wins; ties go to the option with the gentlest worst case.
  ranked.sort((a, b) => a.score - b.score || a.worstPenalty - b.worstPenalty || a.utcHour - b.utcHour);
  return ranked;
}

/**
 * Suggest a rotation: the kindest hour for each participant in turn.
 *
 * Distributed teams are repeatedly advised to rotate meeting times so the same
 * person is not always the one dialling in at night. This returns one candidate
 * per participant — the hour that is best for them while staying as tolerable as
 * possible for everyone else — deduplicated by hour.
 */
export interface RotationOption {
  utcHour: number;
  /** Index of the slot this option favours */
  favours: number;
  ranked: RankedHour;
}

export function getRotationOptions(slots: LocationSlot[], at: Date = new Date()): RotationOption[] {
  if (slots.length < 2) return [];

  const ranked = rankMeetingHours(slots, at);
  const options: RotationOption[] = [];
  const seenHours = new Set<number>();

  for (let i = 0; i < slots.length; i++) {
    // Among hours inside this person's working day, take the one with the
    // lowest total burden. If they have no workable hour at all, skip them.
    const candidates = ranked.filter(r => r.perSlot[i]?.status === 'working');
    const best = candidates[0]; // `ranked` is already sorted best-first
    if (!best || seenHours.has(best.utcHour)) continue;
    seenHours.add(best.utcHour);
    options.push({ utcHour: best.utcHour, favours: i, ranked: best });
  }

  return options;
}

/** Get meeting times: full-overlap hours only, in chronological UTC order */
export function getMeetingTimes(
  slots: LocationSlot[],
  at: Date = new Date()
): { utcHour: number; localTimes: string[] }[] {
  const overlap = getOverlapBlocks(slots, at);
  const times: { utcHour: number; localTimes: string[] }[] = [];

  for (let utcHour = 0; utcHour < 24; utcHour++) {
    if (overlap[utcHour]) {
      const localTimes = slots.map(slot => {
        const offset = getCurrentOffset(slot.timezone, at);
        return formatTime(utcHour, offset);
      });
      times.push({ utcHour, localTimes });
    }
  }

  return times;
}

// === DST transitions ===

export interface Transition {
  date: Date;
  kind: 'start' | 'end';
}

/** All DST transitions for a zone between two instants */
export function getTransitions(tz: TimezoneEntry, from: Date, to: Date): Transition[] {
  if (!tz.dst || !tz.dst_start || !tz.dst_end) return [];

  const out: Transition[] = [];
  const startYear = from.getUTCFullYear();
  const endYear = to.getUTCFullYear();

  for (let year = startYear; year <= endYear + 1; year++) {
    for (const [field, kind] of [
      [tz.dst_start, 'start'],
      [tz.dst_end, 'end'],
    ] as [string, 'start' | 'end'][]) {
      const d = parseDSTDate(field, year);
      if (d && d > from && d <= to) out.push({ date: d, kind });
    }
  }

  out.sort((a, b) => a.date.getTime() - b.date.getTime());
  return out;
}

/** Get a DST transition warning for the next N days */
export function getDSTWarning(tz: TimezoneEntry, at: Date = new Date(), days = 30): string | null {
  const horizon = new Date(at.getTime() + days * 24 * 60 * 60 * 1000);
  const transitions = getTransitions(tz, at, horizon);
  const next = transitions[0];
  if (!next) return null;

  const dayCount = Math.round((next.date.getTime() - at.getTime()) / (24 * 60 * 60 * 1000));
  const plural = dayCount === 1 ? '' : 's';
  return next.kind === 'start'
    ? `DST starts in ${dayCount} day${plural} (clocks spring forward — meetings will shift earlier)`
    : `DST ends in ${dayCount} day${plural} (clocks fall back — meetings will shift later)`;
}

// === Recurring meeting audit ===
//
// A recurring meeting is anchored to one location's local time — the "lead time
// zone". Everyone else's local time then drifts as DST transitions land on
// different dates in different countries. This replays the next N months and
// reports only the dates where somebody's local time actually changes.

export interface AuditRow {
  /** First date on which this set of local times applies */
  date: Date;
  /** UTC hour the meeting falls at during this stretch (may be fractional) */
  utcHour: number;
  localTimes: LocalTime[];
  /** Slot indices whose local time changed at this date (empty for the first row) */
  changedIndices: number[];
}

export function auditRecurring(
  slots: LocationSlot[],
  anchorIndex: number,
  anchorLocalHour: number,
  from: Date = new Date(),
  months = 12
): AuditRow[] {
  const anchor = slots[anchorIndex];
  if (!anchor) return [];

  const rows: AuditRow[] = [];
  const end = new Date(from.getTime());
  end.setUTCMonth(end.getUTCMonth() + months);

  let prevKey = '';
  const cursor = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate(), 12));

  while (cursor <= end) {
    const anchorOffset = getCurrentOffset(anchor.timezone, cursor);
    // The meeting is pinned to anchorLocalHour in the anchor city.
    const utcHour = anchorLocalHour - anchorOffset;

    const raw = slots.map(slot => toLocal(utcHour, getCurrentOffset(slot.timezone, cursor)));
    // Day markers read relative to the anchor city's day, since that is the day
    // the meeting is described in ("Tuesday 9 AM London").
    const anchorDay = raw[anchorIndex]?.dayOffset ?? 0;
    const localTimes = raw.map(lt => ({ minutes: lt.minutes, dayOffset: lt.dayOffset - anchorDay }));
    const key = localTimes.map(lt => `${lt.minutes}:${lt.dayOffset}`).join('|');

    if (key !== prevKey) {
      const changedIndices: number[] = [];
      const prev = rows[rows.length - 1];
      if (prev) {
        for (let i = 0; i < localTimes.length; i++) {
          const a = prev.localTimes[i];
          const b = localTimes[i];
          if (a && b && (a.minutes !== b.minutes || a.dayOffset !== b.dayOffset)) {
            changedIndices.push(i);
          }
        }
      }
      rows.push({
        date: new Date(cursor.getTime()),
        utcHour: ((utcHour % 24) + 24) % 24,
        localTimes,
        changedIndices,
      });
      prevKey = key;
    }

    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return rows;
}

// === Display helpers ===

/** Get the local time string for a timezone at a given instant */
export function getCurrentTimeString(tz: TimezoneEntry, at: Date = new Date()): string {
  const offset = getCurrentOffset(tz, at);
  const localMs = at.getTime() + offset * 3600000;
  const localDate = new Date(localMs);

  const hours = localDate.getUTCHours();
  const minutes = localDate.getUTCMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return `${displayHour}:${String(minutes).padStart(2, '0')} ${ampm} ${getAbbr(tz, at)}`;
}

/** Get the local date string for a timezone at a given instant */
export function getCurrentDateString(tz: TimezoneEntry, at: Date = new Date()): string {
  const offset = getCurrentOffset(tz, at);
  const localDate = new Date(at.getTime() + offset * 3600000);

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[localDate.getUTCDay()]}, ${months[localDate.getUTCMonth()]} ${localDate.getUTCDate()}`;
}

/** Format a UTC offset as "UTC+5:30" */
export function formatOffset(offset: number): string {
  const sign = offset < 0 ? '−' : '+';
  const abs = Math.abs(offset);
  const hours = Math.floor(abs);
  const minutes = Math.round((abs - hours) * 60);
  return minutes === 0 ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

/** Fuzzy search cities by name */
export function fuzzySearchCities(query: string, cities: CityEntry[], limit: number = 8): CityEntry[] {
  if (!query.trim()) return [];

  const q = query.toLowerCase().trim();
  const results: { city: CityEntry; score: number }[] = [];

  for (const city of cities) {
    const name = city.name.toLowerCase();
    const country = city.country.toLowerCase();

    if (name === q) {
      results.push({ city, score: 100 });
      continue;
    }
    if (name.startsWith(q)) {
      results.push({ city, score: 80 });
      continue;
    }
    if (name.includes(q)) {
      results.push({ city, score: 60 });
      continue;
    }
    if (country.includes(q) || city.timezone.toLowerCase().includes(q)) {
      results.push({ city, score: 40 });
      continue;
    }

    // Fuzzy: check if most characters match in order
    let matchIdx = 0;
    for (const ch of name) {
      if (matchIdx < q.length && ch === q[matchIdx]) {
        matchIdx++;
      }
    }
    if (matchIdx >= q.length - 1 && q.length > 1) {
      results.push({ city, score: 20 });
    }
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit).map(r => r.city);
}

/** Look up a timezone by its IANA id */
export function lookupTimezone(timezones: TimezoneEntry[], id: string): TimezoneEntry | undefined {
  return timezones.find(tz => tz.id === id);
}
